import PhoneNumber from 'awesome-phonenumber'
import { RegisterOptions } from 'react-hook-form'
import { FormValues } from '.'
import { isOver16YearsOld, isOver18YearsOld, isValidDob } from '@/helpers/dates'

export type RequiredFieldKey = keyof Omit<FormValues, 'middleName' | 'mobileMeta'>

export const errorMessages = (isScottishResident: boolean) =>
  ({
    firstName: { required: 'Please enter your first name(s).' },
    lastName: { required: 'Please enter your last name(s).' },
    dob: {
      required: 'Please enter your date of birth.',
      invalid: 'Please enter a valid date of birth.',
      tooYoung: `You must be over ${
        isScottishResident === true ? '16' : '18'
      } to submit a reference.`,
    },
    mobile: {
      required: 'Please enter your phone number.',
      invalid: 'Please enter a valid phone number.',
    },
    nationality: { required: 'Please select your nationality.' },
  } satisfies Record<
    RequiredFieldKey,
    { required: string; invalid?: string; tooYoung?: string }
  >)

export type Rules<T extends keyof FormValues> = Omit<
  RegisterOptions<FormValues, T>,
  'setValueAs' | 'disabled' | 'valueAsNumber' | 'valueAsDate'
>

const requiredText = (message: string): Rules<'firstName' | 'lastName'> => ({
  required: message,
  validate: value => (value ?? '').trim().length > 0 || message,
})

const isValidDate = (dob: string) => !Number.isNaN(new Date(dob).getTime())

export const isTooYoung = (dob: string, isScottishResident: boolean) =>
  isScottishResident ? !isOver16YearsOld(dob) : !isOver18YearsOld(dob)

export const getValidationRules = ({
  isScottishResident,
}: {
  isScottishResident: boolean
}) => {
  const messages = errorMessages(isScottishResident)

  const dob: Rules<'dob'> = {
    required: messages.dob.required,
    validate: {
      invalid: value => (isValidDate(value) && isValidDob(value)) || messages.dob.invalid,
      tooYoung: value => !isTooYoung(value, isScottishResident) || messages.dob.tooYoung,
    },
  }

  const mobile: Rules<'mobile'> = {
    required: messages.mobile.required,
    validate: (value, { mobileMeta }) =>
      new PhoneNumber(value.trim(), mobileMeta.iso2).isValid() || messages.mobile.invalid,
  }

  const nationality: Rules<'nationality'> = {
    validate: value => Boolean(value) || messages.nationality.required,
  }

  return {
    firstName: requiredText(messages.firstName.required),
    lastName: requiredText(messages.lastName.required),
    dob,
    mobile,
    nationality,
  }
}
